import { useMutation } from "@tanstack/react-query";

import { RestError } from "../../../../../shared/lib/exceptions";
import { CreateAccountBody, CreateAccountResponse } from "../../../public/supplier-types";
import { createAccount } from "./supplier-registration.api";

export type CreateAccountError = 400 | 403 | 409 | 503 | null;

// 403 → token/email mismatch; 409 → invite already used to register; 400 → validation failure
// (class-validator on RegisterSupplierDto); anything else → 503 (generic error).
const toCreateAccountError = (error: unknown): CreateAccountError => {
  if (
    error instanceof RestError &&
    (error.statusCode === 400 || error.statusCode === 403 || error.statusCode === 409)
  ) {
    return error.statusCode;
  }
  return error ? 503 : null;
};

// TODO: AIM-2620: remove once backend is ready
const isBackendEmulated = () =>
  new URLSearchParams(window.location.search).get("__emulate_registration_backend___") === "yes";

export const useCreateAccount = (): {
  mutate: (body: CreateAccountBody) => Promise<CreateAccountResponse>;
  isPending: boolean;
  error: CreateAccountError;
} => {
  const { mutateAsync, isPending, error } = useMutation({
    meta: { silentError: true },
    mutationFn: (body: CreateAccountBody) =>
      isBackendEmulated()
        ? Promise.resolve<CreateAccountResponse>({ status: "verification-email-sent" })
        : createAccount(body),
  });

  return { mutate: mutateAsync, isPending, error: toCreateAccountError(error) };
};
